import { useState, useRef, useEffect } from 'preact/hooks'
import { X, FileText, AlertTriangle } from 'lucide-preact'
import type { CampaignFileImportCreateDto } from '@/types/campaignFileImport'
import type { AudienceSearchResultDto } from '@/types/audience'
import { audienceApi } from '@/api/audience'
import { parseValidationErrors, setFormValidationErrors } from '@/utils/errorHandling'
import { TextBoxField, FileUpload, FormActions, DatePicker, Checkbox } from '@/components'
import { useAIForm } from '@/hooks/useAIForm'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { formatDateOnly } from '@/utils/dateTimeFormatters'

interface CampaignListModalProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  clientCompanyId: string
  selectedWorkspaceId: string
  onCampaignListSaved: (result: AudienceSearchResultDto) => void
  /**
   * The campaign's start date, shown as a hint under the targeting start date so whoever is uploading
   * a backdated list has the likely answer to hand. Optional because not every caller has the campaign
   * loaded; without it the hint is simply omitted. Never filled into the field — see the reset effect.
   */
  campaignStartDate?: string | null
}

interface CampaignListFormData {
  Name: string
  Description: string
  /**
   * Whether this upload is a list that was already being targeted before today. Form-only — it is
   * never sent or stored. A targeting start date is only meaningful on a historical upload, so this
   * exists purely to make backdating something someone deliberately asked for rather than something
   * they wandered into; the date itself is the record of it afterwards.
   */
  IsHistoricalUpload: boolean
  TargetingStartDate: string
}

/** An ISO timestamp trimmed to the yyyy-MM-dd a date input expects. */
const toDateInputValue = (isoDate?: string | null) => isoDate ? isoDate.split('T')[0] : ''

/**
 * A date as yyyy-MM-dd in the user's own timezone. Built from local parts rather than
 * `toISOString()`, which converts to UTC and would shift the answer by a day for anyone whose
 * local calendar disagrees with UTC's.
 */
const toLocalDateString = (date: Date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-')

/**
 * The latest date targeting can be said to have started: yesterday. A historical upload is one where
 * targeting already happened, so today is not an answer — if targeting starts today, the upload
 * itself is the start and the field should be left off entirely.
 */
const latestTargetingStart = () => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return toLocalDateString(yesterday)
}

const VALIDATION_CONSTANTS = {
  NAME_MAX_LENGTH: 100,
  DESCRIPTION_MAX_LENGTH: 1000,
}

export function CampaignListModal({
  isOpen,
  onClose,
  campaignId,
  clientCompanyId,
  selectedWorkspaceId,
  onCampaignListSaved,
  campaignStartDate
}: CampaignListModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [businessRuleErrors, setBusinessRuleErrors] = useState<{ Title?: string; Message?: string }[]>([])
  const [showAllErrors, setShowAllErrors] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | undefined>()
  const cleanupRef = useRef<(() => void) | null>(null)

  const assetsBaseUrl = import.meta.env.VITE_ASSETS_BASE_URL
  const sampleFileUrl = `${assetsBaseUrl}/assets/file-upload-sample/sample-file-upload.csv`

  // AI-integrated form
  const { form, getFormButtonState } = useAIForm<CampaignListFormData>({
    mode: 'onChange',
    defaultValues: {
      Name: '',
      Description: '',
      IsHistoricalUpload: false,
      TargetingStartDate: ''
    }
  }, {
    aiContext: {
      campaignId: campaignId,
      location: ChatLocations.CampaignAudience,
      entityType: ChatEntityTypes.Campaign,
      viewMode: ChatViewModes.Create,
    },
    fields: [
      { aiName: 'audienceName', formPath: 'Name', displayName: 'Audience Name', fieldType: 'text', maxLength: VALIDATION_CONSTANTS.NAME_MAX_LENGTH },
      { aiName: 'audienceDescription', formPath: 'Description', displayName: 'Description', fieldType: 'textarea', maxLength: VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH },
    ],
    contextSuppressed: !isOpen,
    isEditMode: false
  })

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    setError,
    setValue,
    watch,
  } = form

  const isHistoricalUpload = watch('IsHistoricalUpload')
  const maxTargetingStart = latestTargetingStart()

  // Start every upload from a blank form. The targeting start date is deliberately NOT prefilled from
  // the campaign: it moves the CRM pipeline-attribution cutoff, and a prefilled value would move it on
  // every upload where nobody thought about it. That is right for a one-off historical upload and wrong
  // for an audience grown in weekly batches, where it would claim deals created before those companies
  // were targeted at all. The campaign's start date is offered as a hint on the field instead, so
  // backdating stays something someone chose.
  useEffect(() => {
    if (isOpen) {
      reset({ Name: '', Description: '', IsHistoricalUpload: false, TargetingStartDate: '' })
    }
  }, [isOpen, reset])

  // Un-checking clears the date and revalidates: a value left behind in a field nobody can see must
  // not reach the payload, and the "required" error it may be carrying has to come off the form or
  // the save button stays disabled with nothing on screen to explain why. Checking deliberately does
  // NOT validate — the field has only just appeared, and meeting it with a red error already on it
  // reads as a mistake the person hasn't had the chance to make yet. React Hook Form still recomputes
  // isValid on the change, so the save button disables until they fill it in.
  useEffect(() => {
    if (!isHistoricalUpload) {
      setValue('TargetingStartDate', '', { shouldValidate: true })
    }
  }, [isHistoricalUpload, setValue])

  // The campaign's own start date, surfaced as a hint rather than filled in. It is usually the answer
  // for a list being uploaded after the campaign ran, but only the person uploading knows whether it
  // is the answer for this list.
  const campaignStartHint = toDateInputValue(campaignStartDate)
  const targetingStartHelperText = 'Must be a date before today.'
    + (campaignStartHint ? ` This campaign started ${formatDateOnly(campaignStartHint)}.` : '')

  // Cleanup on unmount or when modal closes
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [])

  const handleClose = () => {
    // Cancel any in-flight requests
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = null
    }
    
    reset()
    setBusinessRuleErrors([])
    setShowAllErrors(false)
    setSelectedFile(null)
    setFileError(undefined)
    setIsSubmitting(false)
    onClose()
  }

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file)
    if (file) {
      setFileError(undefined)
      clearErrors('root.file')
    }
  }

  const handleFileError = (error: string) => {
    setFileError(error)
    setError('root.file', { type: 'manual', message: error })
  }

  const onSubmit = async (data: CampaignListFormData) => {
    if (!selectedFile) {
      setError('root.file', { type: 'manual', message: 'Please select a file to upload.' })
      return
    }

    setIsSubmitting(true)
    let cancelled = false

    // Store cleanup function
    cleanupRef.current = () => {
      cancelled = true
    }

    try {
      const createDto: CampaignFileImportCreateDto = {
        CampaignId: campaignId,
        ClientCompanyId: clientCompanyId,
        WorkspaceId: selectedWorkspaceId,
        Name: data.Name.trim(),
        Description: data.Description?.trim() || undefined,
        // Omit rather than send an empty string: absent means "the upload is the start", and the API
        // derives the cutoff from the row dates exactly as it did before this field existed. Gated on
        // the checkbox as well as the value so an unchecked box can never submit a date, whatever
        // state the field was left in.
        TargetingStartDate: data.IsHistoricalUpload ? data.TargetingStartDate || undefined : undefined
      }

      const response = await audienceApi.create(clientCompanyId, selectedWorkspaceId, createDto, selectedFile)

      if (cancelled) return

      if (response.error) {
        // Global handler shows toasts for 422 validation errors
        if (response.status === 422) {
          // Parse validation errors for custom display
          const validationErrors = parseValidationErrors(response)
          setBusinessRuleErrors(validationErrors)

          // Set form-level errors for any field-specific errors
          setFormValidationErrors(response, setError)

          setError('root.submit', { type: 'manual', message: 'Some rows failed validation.' })
        } else {
          // For non-422 errors, set root error
          setError('root.submit', { type: 'manual', message: response.error })
        }
        return
      }

      if (response.data) {
        onCampaignListSaved(response.data)
        handleClose()
      }
    } catch (error) {
      if (!cancelled) {
        setError('root.submit', { type: 'manual', message: 'Failed to create audience. Please try again.' })
      }
    } finally {
      if (!cancelled) {
        setIsSubmitting(false)
        cleanupRef.current = null
      }
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-600">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">New Audience</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">Upload a CSV file to create a new audience</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/*
          Scrolls in its own right rather than growing the card. Without `overflow-y-auto` this body is a
          flex item whose automatic minimum size is its content height, so it refuses to shrink inside the
          card's `max-h-[90vh]` and a tall form spills out past the rounded edge, taking the action buttons
          off screen with it. That stayed invisible until the validation details list started rendering.
        */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="campaign-list-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <FileUpload
              label="File Upload"
              required
              accept=".csv,text/csv,application/csv"
              maxSizeMB={10}
              selectedFile={selectedFile}
              onFileChange={handleFileChange}
              onError={handleFileError}
              error={fileError}
              helperText="Choose CSV file to upload"
              sampleFileUrl={sampleFileUrl}
              sampleFileName="Sample File Upload File.csv"
            />

            <TextBoxField
              control={control}
              name="Name"
              label="Audience Name"
              required
              showCharCounter
              maxLength={VALIDATION_CONSTANTS.NAME_MAX_LENGTH}
              placeholder="Enter a descriptive name for this audience"
              rules={{
                required: 'Name is required',
                maxLength: { value: VALIDATION_CONSTANTS.NAME_MAX_LENGTH, message: `Name cannot exceed ${VALIDATION_CONSTANTS.NAME_MAX_LENGTH} characters` },
                validate: (value) => {
                  const trimmed = (value ?? '').trim()
                  if (!trimmed) return 'Name cannot be empty or just whitespace'
                  return true
                }
              }}
            />

            <TextBoxField
              control={control}
              name="Description"
              label="Description"
              multiline
              rows={3}
              showCharCounter
              maxLength={VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH}
              placeholder="Optional description of this audience"
              rules={{
                maxLength: { value: VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH, message: `Description cannot exceed ${VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH} characters` }
              }}
            />

            {/*
              Deliberately in the open rather than behind an "Advanced" disclosure. Hiding it is what
              this section is for — the date moves the CRM pipeline-attribution cutoff, so it must not
              be set by accident — but a disclosure hides the question itself, and someone uploading a
              list from a campaign that already ran never learns there was one to answer. The checkbox
              asks it out loud and the date stays unreachable until they answer yes.
            */}
            <div className="border-t border-slate-200 dark:border-slate-600 pt-4 space-y-4">
              <Checkbox
                label="This is a historical upload"
                description="Turn this on if the list was already being targeted before today, so deals already in your CRM still count towards this campaign's pipeline. Leave it off for a list being uploaded as targeting begins."
                {...register('IsHistoricalUpload')}
              />

              {isHistoricalUpload && (
                // Indented to the checkbox's label so it reads as the answer to it rather than a
                // sibling field: pl-7 clears the 1rem box plus the label's 0.75rem gap.
                <div className="pl-7">
                  <DatePicker
                    label="Targeting Start Date"
                    required
                    max={maxTargetingStart}
                    error={errors.TargetingStartDate?.message as string}
                    helperText={targetingStartHelperText}
                    {...register('TargetingStartDate', {
                      // Reads the checkbox off the live form values rather than closing over it. The
                      // field stays registered after it unmounts, so a captured value would strand the
                      // form on rules from the last render it was visible for.
                      validate: (value, formValues) => {
                        if (!formValues.IsHistoricalUpload) return true
                        if (!value) return 'Enter the date targeting actually started'
                        return value <= maxTargetingStart
                          || 'Targeting must have started before today'
                      }
                    })}
                  />
                </div>
              )}
            </div>

            {errors.root?.submit && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium">{errors.root.submit.message}</p>
                </div>
                {businessRuleErrors.length > 0 && (
                  <div className="border border-red-200 dark:border-red-800 rounded-lg bg-white dark:bg-slate-800/50">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-red-100 dark:border-red-800/60">
                      <p className="text-xs font-semibold tracking-wide text-red-700 dark:text-red-300 uppercase">Validation Details</p>
                      {businessRuleErrors.length > 5 && (
                        <button
                          type="button"
                          onClick={() => setShowAllErrors(s => !s)}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline"
                        >
                          {showAllErrors ? 'Show less' : `Show all (${businessRuleErrors.length})`}
                        </button>
                      )}
                    </div>
                    {/*
                      Capped only once expanded. Collapsed, the list is five entries at most, so it fits
                      the modal's own scroll and gets to render whole; capping it there put a second
                      scrollbar inches from the first and cut the fifth entry in half — usually the note
                      that validation stopped early, which is the one worth reading. Expanded it can run
                      to whatever the API returned, so the cap comes back to keep it off the whole modal.
                    */}
                    <ul className={`divide-y divide-red-100 dark:divide-red-800/40 text-sm ${showAllErrors ? 'max-h-56 overflow-auto' : ''}`}>
                      {(showAllErrors ? businessRuleErrors : businessRuleErrors.slice(0, 5)).map((e, i) => (
                        <li key={i} className="px-3 py-2">
                          <p className="text-red-700 dark:text-red-300 font-medium leading-snug">
                            {e.Title || 'Error'}
                          </p>
                          <p className="text-red-600 dark:text-red-400 text-xs break-words whitespace-pre-wrap">
                            {e.Message}
                          </p>
                        </li>
                      ))}
                    </ul>
                    {businessRuleErrors.length > 5 && !showAllErrors && (
                      <div className="px-3 py-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 border-t border-red-100 dark:border-red-800/40">
                        Showing first 5 of {businessRuleErrors.length} errors.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        <FormActions
          isSubmitting={isSubmitting}
          isSaveDisabled={!selectedFile || getFormButtonState(isSubmitting).isSaveDisabled}
          onCancel={handleClose}
          saveText={isSubmitting ? 'Creating...' : 'Create Audience'}
          formId="campaign-list-form"
        />
      </div>
    </div>
  )
}
